import { RelayClient, RelayerTxType } from '@polymarket/builder-relayer-client';
import { BuilderConfig } from '@polymarket/builder-signing-sdk';
import axios from 'axios';
import { encodeFunctionData, type WalletClient } from 'viem';
import { erc20Abi } from 'viem';

import { TypedData } from '@/types/withdrawal.types';

import { RELAYER_URL, SIGNING_SERVER_URL } from '@/constants/api.constants';
import {
  COLLATERAL_OFFRAMP_ADDRESS,
  PUSD_CONTRACT_ADDRESS,
  USDC_E_CONTRACT_ADDRESS,
} from '@/constants/tokens.constants';

export interface RelaySubmitResult {
  transactionHash: string;
}

/**
 * Standardizes error messages for the consumer.
 */
function formatError(err: any): Error {
  const message = err instanceof Error ? err.message : String(err);
  const rawMsg = message?.toLowerCase();

  if (rawMsg.includes('401') || rawMsg.includes('unauthorized')) {
    return new Error(
      'Relayer Authentication Failed (401). Please verify your HMAC backend configuration.',
    );
  }

  if (rawMsg.includes('insufficient funds') || rawMsg.includes('balance')) {
    return new Error('Insufficient funds in the Safe proxy or relayer account.');
  }

  return err instanceof Error ? err : new Error(message);
}
const FALLBACK_RELAYER_URL = 'https://relayer-v2.polymarket.com';
/**
 * Initializes BuilderConfig for HMAC signing if enabled.
 */
export function getBuilderConfig(accessToken: string): BuilderConfig | undefined {
  if (!accessToken) return undefined;

  return new BuilderConfig({
    remoteBuilderConfig: {
      url: SIGNING_SERVER_URL,
      token: accessToken,
    },
  });
}

/**
 * Creates the specialized RelayClient from the Polymarket SDK.
 */
function createRelayerClient(
  walletClient: WalletClient,
  builderConfig?: BuilderConfig,
): RelayClient {
  return new RelayClient(
    RELAYER_URL || FALLBACK_RELAYER_URL,
    137,
    walletClient,
    builderConfig,
    RelayerTxType.SAFE,
  );
}

/**
 * Internal validator for transaction data.
 */
function validateTypedData(typedData: TypedData) {
  if (!typedData || !typedData.message) {
    throw new Error(`Invalid transaction payload: typedData.message is missing.`);
  }
}

/**
 * Secondary check via Relayer status endpoint to recover missed hashes if wait() returns empty.
 */
async function performSecondaryRecovery(pendingTx: any): Promise<string | undefined> {
  if (!pendingTx) return undefined;
  const relayerId = pendingTx.transactionID;
  if (!relayerId) return undefined;

  const relayerStatusUrl = `${RELAYER_URL}/transaction?id=${relayerId}`;

  try {
    const response = await axios.get(relayerStatusUrl);
    const data = response.data;
    const hash = data.transactionHash || (data[0] && data[0].transactionHash);

    if (hash) {
      return hash;
    }

    const errorMsg = data.error || data.status || (data[0] && data[0].errorMsg);
    if (errorMsg) {
      console.error(`[BridgeWithdrawalRelay] Relayer API Error: ${errorMsg}`);
    }
  } catch (e: any) {
    console.warn('[BridgeWithdrawalRelay] Secondary recovery check failed.', e.message || e);
  }

  return undefined;
}

/**
 * Polls for transaction confirmation and attempts secondary hash recovery if the initial wait fails.
 */
async function waitForConfirmation(pendingTx: any): Promise<string> {
  if (!pendingTx || typeof pendingTx.wait !== 'function') {
    throw new Error('Relayer execution returned an invalid transaction object.');
  }

  const receipt = await pendingTx.wait();
  let txnHash = receipt?.transactionHash;

  if (!txnHash) {
    txnHash = await performSecondaryRecovery(pendingTx);
  }

  if (!txnHash) {
    throw new Error('Polymarket Relayer failed to return a transaction hash after submission.');
  }

  return txnHash;
}

/**
 * Production-ready implementation of the Polymarket Bridge Withdrawal Relay.
 * Handles HMAC Authentication, safe execution, and confirmation recovery.
 */
export const bridgeWithdrawalRelayService = {
  /**
   * Submits a transaction to the Polymarket Relayer.
   */
  async submitTransaction(
    walletClient: WalletClient,
    typedData: TypedData,
    accessToken: string,
  ): Promise<RelaySubmitResult> {
    validateTypedData(typedData);

    const builderConfig = getBuilderConfig(accessToken);
    const relayerClient = createRelayerClient(walletClient, builderConfig);

    const txns = [];

    // V2-MIGRATION logic: Detect if the backend is trying to transfer USDC.e directly.
    // Since users now hold pUSD collateral, we must prepend an 'unwrap' call to the
    // CollateralOnramp contract to convert pUSD -> USDC.e before the transfer.
    const isUsdcE = typedData.message.to?.toLowerCase() === USDC_E_CONTRACT_ADDRESS?.toLowerCase();
    const isTransfer = typedData.message.data.startsWith('0xa9059cbb');

    if (isUsdcE && isTransfer) {
      try {
        // Extract amount from ERC20 transfer data (last 32 bytes of the 68-byte data)
        const amountHex = '0x' + typedData.message.data.slice(-64);
        const amount = BigInt(amountHex);

        // Step 1: Approve CollateralOfframp to spend pUSD from the Safe
        const approveData = encodeFunctionData({
          abi: erc20Abi,
          functionName: 'approve',
          args: [COLLATERAL_OFFRAMP_ADDRESS as `0x${string}`, amount],
        });

        txns.push({
          to: PUSD_CONTRACT_ADDRESS as `0x${string}`,
          data: approveData,
          value: '0',
        });

        // Step 2: Unwrap pUSD -> USDC.e
        const unwrapData = encodeFunctionData({
          abi: [
            {
              name: 'unwrap',
              type: 'function',
              stateMutability: 'nonpayable',
              inputs: [
                { name: 'underlyingToken', type: 'address' },
                { name: 'recipient', type: 'address' },
                { name: 'amount', type: 'uint256' },
              ],
              outputs: [],
            },
          ],
          args: [
            USDC_E_CONTRACT_ADDRESS as `0x${string}`,
            typedData.domain.verifyingContract as `0x${string}`, // Safe receives the unwrapped USDC.e
            amount,
          ],
        });

        txns.push({
          to: COLLATERAL_OFFRAMP_ADDRESS as `0x${string}`,
          data: unwrapData,
          value: '0',
        });
      } catch (e) {
        console.warn('[BridgeWithdrawalRelay] Failed to prepend unwrap logic:', e);
      }
    }

    txns.push({
      to: typedData.message.to,
      data: typedData.message.data,
      value: typedData.message.value ?? '0',
    });

    try {
      const pendingTx = await relayerClient.execute(txns);
      const finalTxHash = await waitForConfirmation(pendingTx);

      return { transactionHash: finalTxHash };
    } catch (err: any) {
      console.error('[BridgeWithdrawalRelay] Execution failed:', err);
      throw formatError(err);
    }
  },
};
